import React from "react";
import {CategoryBlockData} from "../types";
import {DashboardButton, DashboardIconButton} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import {getReportYears} from "@/ReactInit/Scripts/DashboardPage/utils/reportYears";

interface ReportSummaryViewProps {
  categoryBlocks: CategoryBlockData[];
  comment: string;
  setComment: (text: string) => void;

  prevStep: () => void;
  submitFinalReport: () => void;
  submitStatus: "idle" | "loading" | "success" | "error";
  showEditCancelButton?: boolean;
  editCancelButton: () => void;
}

const ReportSummaryView: React.FC<ReportSummaryViewProps> = (
  {
    categoryBlocks,
    comment,
    setComment,
    prevStep,
    submitFinalReport,
    submitStatus,
    showEditCancelButton = false,
    editCancelButton,
  }) => {
  const { currentYear, previousYear } = getReportYears();

  const formatNumberForDisplay = (value: string) => {
    if (!value) return "";
    const withSpaces = value.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${withSpaces} kg`;
  };

  return (
    <div className="flex flex-col gap-10 px-4 py-10 bg-brown-50 rounded-3xl tablet:px-10">
      <div className="flex flex-col gap-3">
        <h2 className="text-4xl font-semibold leading-tight tablet:font-5xl">
          Bekreftelse av rapporten
        </h2>
        {/* TODO: Get description text */}
        <p className="text-xl leading-normal">* Skjemabeskrivelse *</p>
      </div>

      <div>
        <h3 className="mb-3 text-2xl font-semibold leading-7 tablet:text-3xl">
          {currentYear}-rapport
        </h3>

        {/* Report summary mobile */}
        <div className="flex flex-col tablet:hidden">
          {categoryBlocks.flatMap((block) =>
            block.products.map((product, idx) => (
              <div
                id={`${block.id}-${idx}`}
                key={`${block.id}-${idx}`}
                className="grid w-full grid-rows-4 py-2 border-b border-disabled-bg"
              >
                <div className="py-2 text-base leading-normal px-0.5 tablet:px-4">
                  <p className="inline-block w-1/2 mr-2 font-semibold text-black-subtile">
                    Produkt
                  </p>
                  {product}
                </div>
                <div className="py-2 text-base leading-normal px-0.5 tablet:px-4">
                  <p className="inline-block w-1/2 mr-2 font-semibold text-black-subtile">
                    Produktkategori
                  </p>
                  {block.category}
                </div>
                <div className="py-2 text-base font-semibold leading-normal px-0.5 tablet:px-4">
                  <p className="inline-block w-1/2 mr-2 font-semibold text-black-subtile">
                    Volum i {currentYear}
                  </p>
                  {formatNumberForDisplay(block.volumes[idx])}
                </div>
                <div className="py-2 text-base leading-normal px-0.5 text-black-subtile tablet:px-4">
                  <p className="inline-block w-1/2 mr-2 font-semibold text-black-subtile">
                    Volum i {previousYear}
                  </p>
                  {block.lastYearVolumes[idx] != null
                    ? `${block.lastYearVolumes[idx]} kg`
                    : "—"}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Report summary large screens  */}
        <div className="hidden tablet:block">
          <div className="grid w-full grid-cols-4 border-b border-disabled-bg">
            <p className="py-4 text-base font-semibold leading-normal px-0.5 text-black-subtile tablet:px-4">
              Produkt
            </p>
            <p className="py-4 text-base font-semibold leading-normal px-0.5 text-black-subtile tablet:px-4">
              Produktkategori
            </p>
            <p className="py-4 text-base font-semibold leading-normal px-0.5 text-black-subtile tablet:px-4">
              Volum i {currentYear}
            </p>
            <p className="py-4 text-base font-semibold leading-normal px-0.5 text-black-subtile tablet:px-4">
              Volum i {previousYear}
            </p>
          </div>
          {categoryBlocks.flatMap((block) =>
            block.products.map((product, idx) => (
              <div
                id={`${block.id}-${idx}`}
                key={`${block.id}-${idx}`}
                className="grid w-full grid-cols-4 border-b border-disabled-bg"
              >
                <div className="py-4 text-base leading-normal px-0.5 tablet:px-4">
                  {product}
                </div>
                <div className="py-4 text-base leading-normal px-0.5 tablet:px-4">
                  {block.category}
                </div>
                <div className="py-4 text-base font-semibold leading-normal px-0.5 tablet:px-4">
                  {formatNumberForDisplay(block.volumes[idx])}
                </div>
                <div className="py-4 text-base leading-normal px-0.5 text-black-subtile tablet:px-4">
                  {block.lastYearVolumes[idx] != null
                    ? `${block.lastYearVolumes[idx]} kg`
                    : "—"}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="comment"
          className="text-xl font-semibold leading-8 tablet:text-2xl"
        >
          Kommentar
        </label>
        <div
          className="flex w-full h-24 gap-3 p-3 bg-white border-2 border-brown-primary rounded-xl tablet:h-36 tablet:pl-6 tablet:pr-3">
          <div className="pointer-events-none icon-a-arrow-down !text-3xl !leading-none h-fit text-black-subtile py-1" aria-hidden="true"/>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full text-xl leading-normal resize-none placeholder:text-base tablet:placeholder:text-xl tablet:placeholder:py-1 py-1 px-1.5"
            placeholder="Skriv inn kommentar (valgfritt)"
          />

          <DashboardIconButton
            label="Fjern kommentar"
            variant="ghost"
            rounded="full"
            className="h-fit"
            onClick={() => {
              setComment("");
            }}
            icon="icon-x"
          />
        </div>
      </div>

      <div className="flex flex-col items-center justify-center gap-3 tablet:flex-row">
        {showEditCancelButton ? (
          <DashboardButton
            label="Avbryt redigering"
            variant="underline"
            icon="icon-x"
            onClick={editCancelButton}
          />
        ) : (
          <DashboardButton
            label="Gå tilbake"
            variant="underline"
            icon="icon-arrow-left"
            onClick={prevStep}
          />
        )}
        <DashboardButton
          label="Fullfør rapportering"
          variant="darkplain"
          onClick={submitFinalReport}
          icon="icon-check"
          loading={submitStatus === "loading"}
          disabled={submitStatus === "loading"}
          loadingText="Sender…"
          minLoadingMs={1000}
        />
      </div>
    </div>
  )
    ;
};

export default ReportSummaryView;
